import React, {
  ReactNode,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef as useReactRef,
  useState,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { getOnboardingColors } from './colors';

const useStyles = makeStyles(theme => {
  const c = getOnboardingColors(theme);
  return {
    dialog: {
      '& .MuiDialog-paper': {
        borderRadius: 8,
        maxWidth: 600,
        width: '100%',
        margin: theme.spacing(2),
        overflow: 'hidden',
      },
    },
    header: {
      backgroundColor: c.modalHeaderBg,
      color: c.modalHeaderFg,
      padding: theme.spacing(1.5, 2),
      position: 'relative',
    },
    headerSubtitle: {
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
      fontWeight: 400,
      opacity: 0.85,
      marginBottom: theme.spacing(1),
    },
    headerTitle: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 700,
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(2.5),
      right: theme.spacing(2),
      color: c.modalHeaderFg,
    },
    content: {
      padding: theme.spacing(2, 3, 0),
    },
    stepIndicator: {
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
      color: theme.palette.text.secondary,
      fontWeight: 700,
      marginBottom: theme.spacing(1),
    },
    stepTitle: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 700,
      marginBottom: theme.spacing(1),
    },
    actions: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 2),
    },
    backButton: {
      fontWeight: 600,
      textTransform: 'none',
      borderColor: theme.palette.divider,
      borderRadius: 4,
      padding: theme.spacing(1, 2),
    },
    continueButton: {
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      color: c.buttonPrimaryFg,
    },
    spotlightOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 1300,
      pointerEvents: 'none',
    },
    spotlightBackdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 1300,
      pointerEvents: 'auto',
    },
    spotlightHole: {
      position: 'fixed',
      zIndex: 1301,
      borderRadius: 8,
      pointerEvents: 'auto',
      transition:
        'top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease',
    },
    tooltipCard: {
      position: 'fixed',
      zIndex: 1302,
      backgroundColor: theme.palette.background.paper,
      borderRadius: 8,
      width: 520,
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: theme.shadows[16],
      overflow: 'hidden',
      transition: 'top 0.3s ease, left 0.3s ease',
    },
    tooltipContent: {
      padding: theme.spacing(2, 3, 0),
      overflowY: 'auto',
      flex: 1,
      minHeight: 0,
    },
    tooltipActions: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 2),
    },
  };
});

export interface StepModalProps {
  open: boolean;
  onClose: () => void;
  journeyTitle: string;
  sectionTitle: string;
  currentStep: number;
  totalSteps: number;
  stepTitle: string;
  children: ReactNode;
  onBack?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  backLabel?: string;
  /** Override the "Step X/Y" indicator text */
  stepIndicatorLabel?: string;
  /** When true, hides the default Back/Continue buttons (step renders its own) */
  hideActions?: boolean;
  /** When true, hides the step indicator and just shows the title/content */
  hideSteps?: boolean;
  /** When true, hides the step title below the indicator */
  hideStepTitle?: boolean;
  /** When true, centers the step content */
  centerContent?: boolean;
  /** When provided, renders as a spotlight tooltip anchored to this element */
  targetRef?: RefObject<HTMLElement>;
  /** Preferred side of the target to place the tooltip */
  placement?: 'left' | 'right' | 'bottom' | 'top';
  /** Padding around the spotlight hole */
  spotlightPadding?: number;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const useTargetRect = (
  targetRef: RefObject<HTMLElement> | undefined,
  open: boolean,
): Rect | null => {
  const [rect, setRect] = useState<Rect | null>(null);
  const clearTimerRef = useReactRef<ReturnType<typeof setTimeout> | null>(null);

  const updateRect = useCallback(() => {
    if (targetRef?.current) {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
      const r = targetRef.current.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      return;
    }

    if (!targetRef) {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
      setRect(null);
      return;
    }

    if (!clearTimerRef.current) {
      clearTimerRef.current = setTimeout(() => {
        clearTimerRef.current = null;
        setRect(null);
      }, 1500);
    }
  }, [targetRef]);

  useEffect(() => {
    if (!open) {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
      setRect(null);
      return () => {};
    }

    updateRect();

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [open, targetRef, updateRect]);

  useEffect(
    () => () => {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
      }
    },
    [],
  );

  return rect;
};

const computeTooltipPosition = (
  targetRect: Rect,
  placement: 'left' | 'right' | 'bottom' | 'top',
  padding: number,
  tooltipWidth: number,
) => {
  const gap = 16;
  const holeLeft = targetRect.left - padding;
  const holeTop = targetRect.top - padding;
  const holeWidth = targetRect.width + padding * 2;
  const holeHeight = targetRect.height + padding * 2;

  let top = holeTop;
  let left = 0;

  switch (placement) {
    case 'left':
      left = holeLeft - tooltipWidth - gap;
      break;
    case 'right':
      left = holeLeft + holeWidth + gap;
      break;
    case 'bottom':
      top = holeTop + holeHeight + gap;
      left = holeLeft;
      break;
    case 'top':
      top = holeTop - gap;
      left = holeLeft;
      break;
    default:
      left = holeLeft - tooltipWidth - gap;
  }

  // Clamp to viewport
  if (left < 16) left = 16;
  if (left + tooltipWidth > window.innerWidth - 16) {
    left = window.innerWidth - tooltipWidth - 16;
  }
  if (top < 16) top = 16;

  return { top, left };
};

/**
 * Measures the tooltip after render, clamps it to all 4 viewport edges,
 * and re-clamps on resize / zoom changes.
 */
const useTooltipPlacement = (
  targetRect: Rect | null,
  placement: 'left' | 'right' | 'bottom' | 'top',
  padding: number,
  tooltipWidth: number,
) => {
  const tooltipRef = useReactRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: -9999,
    left: -9999,
  });

  const recompute = useCallback(() => {
    if (!targetRect) return;

    const base = computeTooltipPosition(
      targetRect,
      placement,
      padding,
      tooltipWidth,
    );
    let { top, left } = base;

    const el = tooltipRef.current;
    if (el) {
      const margin = 16;
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const h = el.offsetHeight;
      const w = el.offsetWidth;

      // Clamp bottom
      if (top + h > viewportH - margin) {
        top = Math.max(margin, viewportH - h - margin);
      }
      // Clamp top
      if (top < margin) top = margin;
      // Clamp right
      if (left + w > viewportW - margin) {
        left = Math.max(margin, viewportW - w - margin);
      }
      // Clamp left
      if (left < margin) left = margin;
    }

    setPos(prev =>
      prev.top === top && prev.left === left ? prev : { top, left },
    );
  }, [targetRect, placement, padding, tooltipWidth]);

  // Re-clamp after every commit (layout phase, before paint)
  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  // Re-clamp on resize / zoom
  useEffect(() => {
    if (!targetRect) return () => {};
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [targetRect, recompute]);

  return { tooltipRef, pos };
};

// Shared header/content renderer
const ModalHeader = ({
  journeyTitle,
  sectionTitle,
  onClose,
  classes,
}: {
  journeyTitle: string;
  sectionTitle: string;
  onClose: () => void;
  classes: ReturnType<typeof useStyles>;
}) => (
  <Box className={classes.header}>
    <Typography className={classes.headerSubtitle}>{journeyTitle}</Typography>
    <Typography className={classes.headerTitle}>{sectionTitle}</Typography>
    <IconButton
      className={classes.closeButton}
      onClick={onClose}
      size="small"
      aria-label="close"
    >
      <CloseIcon />
    </IconButton>
  </Box>
);

const ModalBody = ({
  currentStep,
  totalSteps,
  stepTitle,
  stepIndicatorLabel,
  children,
  classes,
  hideSteps,
  hideStepTitle,
  centerContent,
  className,
  contentRef,
  onClick,
}: {
  currentStep: number;
  totalSteps: number;
  stepTitle: string;
  stepIndicatorLabel?: string;
  children: ReactNode;
  classes: ReturnType<typeof useStyles>;
  className: string;
  hideSteps?: boolean;
  hideStepTitle?: boolean;
  centerContent?: boolean;
  contentRef?: RefObject<HTMLElement>;
  onClick?: (e: React.MouseEvent) => void;
}) => (
  <div
    ref={contentRef as React.RefObject<HTMLDivElement> | undefined}
    className={className}
    style={centerContent ? { textAlign: 'center' } : undefined}
    onClick={onClick}
  >
    {!hideSteps && (
      <Typography className={classes.stepIndicator}>
        {stepIndicatorLabel ?? `Step ${currentStep}/${totalSteps}`}
      </Typography>
    )}
    {!hideStepTitle && (
      <Typography className={classes.stepTitle}>{stepTitle}</Typography>
    )}
    {children}
  </div>
);

const ModalActions = ({
  onBack,
  onContinue,
  backLabel,
  continueLabel,
  classes,
  className,
}: {
  onBack?: () => void;
  onContinue: () => void;
  backLabel: string;
  continueLabel: string;
  classes: ReturnType<typeof useStyles>;
  className: string;
}) => (
  <Box className={className}>
    {onBack ? (
      <Button
        variant="outlined"
        color="primary"
        className={classes.backButton}
        onClick={onBack}
      >
        {backLabel}
      </Button>
    ) : (
      <Box />
    )}
    <Button
      variant="contained"
      color="primary"
      className={classes.continueButton}
      onClick={onContinue}
    >
      {continueLabel}
    </Button>
  </Box>
);

export const StepModal = ({
  open,
  onClose,
  journeyTitle,
  sectionTitle,
  currentStep,
  totalSteps,
  stepTitle,
  children,
  onBack,
  onContinue,
  continueLabel = 'Continue',
  backLabel = 'Back',
  stepIndicatorLabel,
  hideActions,
  hideSteps,
  hideStepTitle,
  centerContent,
  targetRef,
  placement = 'left',
  spotlightPadding = 8,
}: StepModalProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const oColors = getOnboardingColors(theme);
  const targetRect = useTargetRect(targetRef, open);
  const isTooltipMode = !!targetRef;

  const { tooltipRef, pos: tooltipPos } = useTooltipPlacement(
    targetRect,
    placement,
    spotlightPadding,
    520,
  );
  const contentRef = useReactRef<HTMLElement>(null);

  // Force all links inside the modal to open in a new tab
  // so users don't lose their onboarding progress
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest(
      'a[href]',
    ) as HTMLAnchorElement | null;
    if (anchor && anchor.href) {
      e.preventDefault();
      e.stopPropagation();
      window.open(anchor.href, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // Reset scroll to top whenever the step changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  if (!open) return null;

  // === Dialog mode (centered modal) ===
  if (!isTooltipMode) {
    return (
      <Dialog open={open} onClose={onClose} className={classes.dialog}>
        <ModalHeader
          journeyTitle={journeyTitle}
          sectionTitle={sectionTitle}
          onClose={onClose}
          classes={classes}
        />
        <DialogContent
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={classes.content}
          style={centerContent ? { textAlign: 'center' } : undefined}
          onClick={handleContentClick}
        >
          {!hideSteps && (
            <Typography className={classes.stepIndicator}>
              {stepIndicatorLabel ?? `Step ${currentStep}/${totalSteps}`}
            </Typography>
          )}
          {!hideStepTitle && (
            <Typography className={classes.stepTitle}>{stepTitle}</Typography>
          )}
          {children}
        </DialogContent>
        {!hideActions && onContinue && (
          <DialogActions className={classes.actions}>
            {onBack ? (
              <Button
                variant="outlined"
                color="primary"
                className={classes.backButton}
                onClick={onBack}
              >
                {backLabel}
              </Button>
            ) : (
              <Box />
            )}
            <Button
              variant="contained"
              color="primary"
              className={classes.continueButton}
              onClick={onContinue}
            >
              {continueLabel}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }

  // === Tooltip / Spotlight mode ===
  if (!targetRect) return null;

  const holeStyle: React.CSSProperties = {
    top: targetRect.top - spotlightPadding,
    left: targetRect.left - spotlightPadding,
    width: targetRect.width + spotlightPadding * 2,
    height: targetRect.height + spotlightPadding * 2,
    boxShadow: `0 0 0 4px ${oColors.spotlightGlow}, 0 0 0 9999px ${oColors.spotlightBackdrop}`,
  };

  return ReactDOM.createPortal(
    <>
      {/* Transparent full-screen click-catcher */}
      <Box className={classes.spotlightBackdrop} onClick={onClose} />

      {/* Transparent hole over target so it stays interactive-looking */}
      <Box className={classes.spotlightHole} style={holeStyle} />

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className={classes.tooltipCard}
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <ModalHeader
          journeyTitle={journeyTitle}
          sectionTitle={sectionTitle}
          onClose={onClose}
          classes={classes}
        />
        <ModalBody
          contentRef={contentRef}
          currentStep={currentStep}
          totalSteps={totalSteps}
          stepTitle={stepTitle}
          hideSteps={hideSteps}
          hideStepTitle={hideStepTitle}
          centerContent={centerContent}
          stepIndicatorLabel={stepIndicatorLabel}
          classes={classes}
          className={classes.tooltipContent}
          onClick={handleContentClick}
        >
          {children}
        </ModalBody>
        {!hideActions && onContinue && (
          <ModalActions
            onBack={onBack}
            onContinue={onContinue}
            backLabel={backLabel}
            continueLabel={continueLabel}
            classes={classes}
            className={classes.tooltipActions}
          />
        )}
      </div>
    </>,
    document.body,
  );
};
